import React from "react";
import Title from "../../ui/Title";

const Countries = () => {
  return (
    <div>
      <Title
        title="How do countries in the Nooraverse fare with respect to gender equality in care?"
        className="mb-6"
      />

      <div id="india" className="mb-8 scroll-mt-24">
        <h3 className="text-xl font-bold text-black mb-4">India</h3>
        <div className="space-y-4 text-softBlack leading-loose text-base font-normal">
          <ul className="list-disc list-outside space-y-2 ml-7">
            <li>
              Time-use surveys are generally used to understand the
              participation of men and women in paid and unpaid activities. A{" "}
              <a
                href="https://www.mospi.gov.in/sites/default/files/publication_reports/TUS_Factsheet_25022025.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue underline"
              >
                2024 Time Use Survey
              </a>{" "}
              conducted by the Government of India, found that females aged six
              years and over spent 19.7 percent of their day on unpaid care and
              domestic work, when compared to 2.6 percent spent by men.
            </li>
            <li>
              The survey found that females aged 6 years and over spent 289
              minutes (4.8 hours) on an average in a day in unpaid domestic
              services for household members, whereas males over the age of 6
              years spent 88 minutes (about 1.5 hours) a day in such activities.
              Females also spent 137 minutes in a day on average (about 2.3
              hours) in caring for their household members compared to 75
              minutes (about 1.25 hours) spent by male members of the household
            </li>
            <li>
              There was little difference in the participation rates in domestic
              and unpaid care work overall between rural and urban areas.
            </li>
            <li>
              A{" "}
              <a
                href="https://www.mospi.gov.in/sites/default/files/press_release/PR_TUS_25022025.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue underline"
              >
                press release
              </a>{" "}
              issued on the Time Use Survey stated that 41 percent of females
              aged 15-59 years participated in caregiving for their household
              members, while male participation in this age group was 21.4
              percent. Female participants in caregiving activities spent about
              140 minutes in a day, compared to 74 minutes spent by male
              participants aged 15-59 years.
            </li>
          </ul>
        </div>
      </div>

      <div id="bangladesh" className="mb-8 scroll-mt-24">
        <h3 className="text-xl font-bold text-black mb-4">Bangladesh</h3>
        <div className="space-y-4 text-softBlack leading-loose text-base font-normal">
          <p>
            The{" "}
            <a
              href="https://data.unwomen.org/sites/default/files/documents/Publications/2024/Bangladesh_TUS-Summary-Brief_0524.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue underline"
            >
              Time Use Survey
            </a>{" "}
            carried out by the Women Count Programme and the Bangladesh Bureau
            of Statistics (BBS) in 2021 reveals that women shoulder a
            significant load of domestic work and caregiving in Bangladesh.
          </p>
          <p>Below are some highlights from the survey:</p>
          <ul className="list-disc list-outside space-y-2 ml-7">
            <li>
              Women spend 7.3 times as much time per day as men on unpaid care
              and domestic work. Women spend 5.9 hours (354 minutes) per day and
              men spend 0.8 hours (48 minutes) per day on unpaid care and
              domestic work.
            </li>
            <li>
              Women are six times as involved as men in caregiving at home in
              terms of care responsibilities and tasks.
            </li>
            <li>
              On average, men and women perceive men’s work to be more
              important. This indicates the reinforcement of traditional
              stereotypes.
            </li>
            <li>
              More than 90 percent of respondents in the time-use survey
              believed that both husband and wife should earn an income for the
              family. However, the traditional stereotypes and gender norms act
              as a barrier to equal distribution of caregiving and domestic
              work.
            </li>
          </ul>
        </div>
      </div>

      {/* Indonesia */}
      <div id="indonesia" className="mb-8 scroll-mt-24">
        <h3 className="text-xl font-bold text-black mb-4">Indonesia</h3>
        <div className="space-y-4 text-softBlack leading-loose text-base font-normal">
          <p>
            {" "}
            <a
              href="https://www.smeru.or.id/sites/default/files/events/24102017_fkp_ucw.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue underline"
            >
              Calculations
            </a>{" "}
            by the SMERU Research Institute, based on the National
            Socio-Economic Household Survey (SUSENAS) and The National Labour
            Force Survey (SAKERNAS), indicate that women bear a disproportionate
            share of unpaid care work compared to men.
          </p>
          <ul className="list-disc list-outside space-y-2 ml-7">
            <li>
              The gender gap in unpaid care work is high. In 2015, the
              percentage of women over 14 years of age, engaged in unpaid care
              work was 85.65 percent, compared to 27.82 percent of men over the
              age of 14. Here, the gender gap denotes the percentage difference
              between women and men engaged in unpaid care work.
            </li>
            <li>
              The data also indicates that a significant percentage of women
              start doing care work from a very young age, compared to men.
            </li>
            <li>
              The gender gap in unpaid care work is wider in low-income groups
              compared to higher-income groups.
            </li>
          </ul>
        </div>
      </div>

      {/* Nepal */}
      <div id="nepal" className="mb-4 scroll-mt-24">
        <h3 className="text-xl font-bold text-black mb-4">Nepal</h3>
        <div className="space-y-4 text-softBlack leading-loose text-base font-normal">
          <ul className="list-disc list-outside space-y-2 ml-7">
            <li>
              According to{" "}
              <a
                href="https://www.ilo.org/resource/news/ilo-un-women-joint-programme-decent-employment-women-care-economy-concludes"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue underline"
              >
                data
              </a>{" "}
              from the ILO, women carry out 85 percent of daily unpaid care work
              in households in Nepal.
            </li>
            <li>
              In Nepal, cumulatively, women spent about 29 million hours a day
              compared to 5 million hours spent by men.
            </li>
            <li>
              Based on the{" "}
              <a
                href="https://pdf.usaid.gov/pdf_docs/PA021FF8.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue underline"
              >
                Time-Use Survey 2022,
              </a>{" "}
              on average, women spend six hours (360 minutes) and men spend 2.25
              hours (135 minutes) per day on domestic tasks.
            </li>
            <li>
              The survey also reveals that the time spent by women in domestic
              and care work is almost three times as much as men.
            </li>
          </ul>
        </div>
      </div>

      <p className="italic text-softBlack leading-loose text-base font-normal">
        Note: The Time-Use Survey 2022, supported by the United States Agency
        for International Development (USAID) is no longer available online. The
        survey involved interviews of one woman and one man from 3,500 sampled
        households in Bagmati and Madhesh Province).
      </p>
    </div>
  );
};

export default Countries;
